import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import useSWR from 'swr';

import {
    type ByoLlmConfiguration,
    ByoLlmConfigurationType,
} from '../../../../services/llm-gateway/LlmGateway.types';
import { getConnectorTemplateFromTags } from '../../../../services/llm-gateway/LlmGateway.utils';
import { getAvailableModelsForByoLlmConfiguration } from '../../../../services/llm-gateway/LlmGatewayService';
import { AutocompleteInputAdornment } from './AutocompleteInputAdornment';
import {
    LabelWithTooltip,
    type TooltipProps,
} from './LabelWithTooltip';

interface AvailableModelsAutocompleteProps {
    organizationId: string;
    tenantId: string;
    byoLlmConfiguration: Partial<ByoLlmConfiguration>;
    label: string;
    initialValue: string;
    required?: boolean;
    disabled?: boolean;
    onChange?: (selectedModel?: string) => void;
    labelTooltip?: TooltipProps;
}

export const AvailableModelsAutocomplete: React.FC<AvailableModelsAutocompleteProps> = ({
    organizationId, tenantId, byoLlmConfiguration, label, initialValue, required, disabled, onChange, labelTooltip,
}: AvailableModelsAutocompleteProps) => {
    const [ value, setValue ] = useState<string>(initialValue);
    const [ selected, setSelected ] = useState<string | null>(null);
    const [ isOpen, setIsOpen ] = useState(false);

    useEffect(() => {
        setValue(initialValue);
        setSelected(initialValue);
    }, [ initialValue ]);

    const resolvedConfiguration = useMemo(() => {
        const config: Partial<ByoLlmConfiguration> = {
            ...byoLlmConfiguration,
            organizationId,
            tenantId,
            configurationType: ByoLlmConfigurationType.SelfServe,
        };

        const connectorTemplate = getConnectorTemplateFromTags(config.connectorTags);
        if (connectorTemplate) {
            config.selfServeByoConfigParameters = { connectorTemplate };
        }

        return config;
    }, [ byoLlmConfiguration, organizationId, tenantId ]);

    const {
        data: models, isLoading: loading,
    } = useSWR(
        resolvedConfiguration?.connectionId
            ? [ organizationId, tenantId, resolvedConfiguration.connectionId ]
            : undefined,
        () => getAvailableModelsForByoLlmConfiguration(organizationId, tenantId, resolvedConfiguration),
    );

    const handleChange = useCallback((_event: React.ChangeEvent<{}>, newValue: string | null) => {
        setSelected(newValue);
        if (onChange) {
            onChange(newValue ?? undefined);
        }
    }, [ onChange ]);

    const toggleOpen = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    return (
        <UiStack direction='column'>
            <LabelWithTooltip
                label={label}
                required={required}
                tooltip={labelTooltip ?? {
                    translationKey: 'CLIENT_BYO_LLM_TOOLTIP_LLM_IDENTIFIER',
                    placement: 'top',
                }}
                dataCy="available-models-autocomplete-label-with-tooltip"
            />
            <Autocomplete
                style={{ width: '350px' }}
                options={models ?? []}
                freeSolo
                autoComplete
                inputValue={value}
                value={selected}
                data-cy="available-models-autocomplete"
                loading={loading}
                open={isOpen}
                disabled={disabled}
                onChange={handleChange}
                onOpen={() => setIsOpen(true)}
                onClose={() => setIsOpen(false)}
                autoSelect
                onInputChange={(event, newValue) => {
                    if (!event) {
                        return;
                    }
                    setValue(newValue);
                }}
                renderInput={(params) => (
                    <TextField
                        required={required}
                        {...params}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <AutocompleteInputAdornment
                                    onClick={toggleOpen}
                                    disabled={disabled}
                                    isAutocompleteOpen={isOpen} />
                            ),
                        }}
                        fullWidth
                    />
                )}
                renderOption={(optProps, option) => (
                    <UiStack
                        p={SpacingToken.S as any}
                        {...optProps as any}>
                        <UiText>
                            {option}
                        </UiText>
                    </UiStack>
                )}
            />
        </UiStack>
    );
};
